import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, Layout, Card, Text, Stack, Button, EmptyState, Banner, ActionList, Tooltip, Frame } from '@shopify/polaris';
import { SystemSettingsService } from '../services/systemSettingsService';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LandingNav from './landing/components/LandingNav';

const MaintenancePage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [maintenanceInfo, setMaintenanceInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bypassActive, setBypassActive] = useState(false);
  
  useEffect(() => {
    // Check if admin has chosen to bypass maintenance
    const bypass = localStorage.getItem('bypassMaintenance') === 'true';
    setBypassActive(bypass);
    
    const fetchMaintenanceInfo = async () => {
      try {
        const info = await SystemSettingsService.getMaintenanceInfo();
        setMaintenanceInfo(info);
      } catch (error) {
        console.error('Error fetching maintenance info:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMaintenanceInfo();
    
    // Refresh every minute
    const interval = setInterval(fetchMaintenanceInfo, 60000);
    return () => clearInterval(interval);
  }, []);
  
  // Format the maintenance date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Handle bypassing maintenance mode
  const handleBypassMaintenance = () => {
    localStorage.setItem('bypassMaintenance', 'true');
    setBypassActive(true);
    window.location.href = '/';
  };
  
  // Handle disabling bypass
  const handleDisableBypass = () => {
    localStorage.removeItem('bypassMaintenance');
    setBypassActive(false);
  };
  
  // If loading, show loading state
  if (loading) {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <Card.Section>
                <Text>Loading...</Text>
              </Card.Section>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }
  
  // If not in maintenance mode and not bypassing, redirect to dashboard
  if (!maintenanceInfo?.maintenanceMode && !bypassActive) {
    return <Navigate to="/" />;
  }
  
  // Calculate estimated end time if available
  const getEstimatedEndTime = () => {
    if (!maintenanceInfo.scheduledMaintenanceEndDate) return null;
    return formatDate(maintenanceInfo.scheduledMaintenanceEndDate);
  };
  
  const estimatedEndTime = getEstimatedEndTime();
  
  const handleNavClick = (path) => {
    navigate(path);
  };
  
  return (
    <Frame>
      <Page>
        <LandingNav currentPath="/" onNavClick={handleNavClick} />

        {/* Maintenance Content */}
        <div style={{ padding: '20px', backgroundColor: '#f6f9fc', minHeight: 'calc(100vh - 70px)' }}>
          <Layout>
            {isAdmin && (
              <Layout.Section>
                <Banner status="info" title="Admin Information">
                  <Stack vertical spacing="tight">
                    <Text>You are viewing this page as an administrator.</Text>
                    {bypassActive ? (
                      <Text>You have bypassed maintenance mode and can access the entire application. Regular users will see this maintenance page.</Text>
                    ) : (
                      <Text>You can bypass maintenance mode to test the application while it remains inaccessible to regular users.</Text>
                    )}
                    <Text>Current maintenance status: <strong>{maintenanceInfo.maintenanceMode ? 'Active' : 'Scheduled'}</strong></Text>
                  </Stack>
                </Banner>
              </Layout.Section>
            )}
            
            <Layout.Section>
                <Card>
                  <Card.Section>
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                      <div style={{ marginBottom: '40px' }}>
                        <div style={{ 
                          backgroundColor: '#f0f6f3', 
                          borderRadius: '50%', 
                          width: '160px', 
                          height: '160px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          margin: '0 auto',
                          boxShadow: '0 6px 12px rgba(0, 0, 0, 0.08)',
                          border: '6px solid #ffffff'
                        }}>
                          <div style={{ fontSize: '70px' }}>🔧</div>
                        </div>
                      </div>
                      
                      <Text variant="headingXl" as="h1" fontWeight="bold" color="text">
                        {maintenanceInfo.maintenanceTitle || "System Maintenance"}
                      </Text>
                      
                      <div style={{ margin: '20px 0', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
                        <Text variant="bodyLg">
                          {maintenanceInfo.maintenanceMessage || "We're currently performing system maintenance to improve your experience."}
                        </Text>
                      </div>
                      
                      {estimatedEndTime && (
                        <div style={{ 
                          margin: '30px auto', 
                          padding: '20px 25px', 
                          backgroundColor: '#f9f9f9', 
                          borderRadius: '8px',
                          maxWidth: '400px',
                          border: '1px solid #e6e6e6',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '28px', marginBottom: '10px' }}>⏱️</div>
                          <Text variant="headingSm" as="h3">Estimated Completion</Text>
                          <Text variant="bodyMd" fontWeight="semibold" style={{ marginTop: '5px' }}>{estimatedEndTime}</Text>
                        </div>
                      )}
                      
                      <div style={{ margin: '20px 0' }}>
                        <Text variant="bodyMd">
                          Please check back later. Thank you for your patience.
                        </Text>
                      </div>
                      
                      {/* Status Indicators */}
                      <div style={{ margin: '30px 0' }}>
                        <Card subdued>
                          <Card.Section>
                            <Text variant="headingSm" as="h3">System Status</Text>
                            <div style={{ marginTop: '15px' }}>
                              <Stack vertical spacing="tight">
                                <Stack alignment="center" distribution="equalSpacing">
                                  <Stack alignment="center" spacing="tight">
                                    <div style={{ 
                                      width: '10px', 
                                      height: '10px', 
                                      borderRadius: '50%', 
                                      backgroundColor: '#de3618',
                                    }}></div>
                                    <Text variant="bodyMd">Main Application</Text>
                                  </Stack>
                                  <Text variant="bodyMd" color="critical">Offline</Text>
                                </Stack>
                                
                                <Stack alignment="center" distribution="equalSpacing">
                                  <Stack alignment="center" spacing="tight">
                                    <div style={{ 
                                      width: '10px', 
                                      height: '10px', 
                                      borderRadius: '50%', 
                                      backgroundColor: '#00a47c',
                                    }}></div>
                                    <Text variant="bodyMd">Website</Text>
                                  </Stack>
                                  <Text variant="bodyMd" color="success">Online</Text>
                                </Stack>
                                
                                <Stack alignment="center" distribution="equalSpacing">
                                  <Stack alignment="center" spacing="tight">
                                    <div style={{ 
                                      width: '10px', 
                                      height: '10px', 
                                      borderRadius: '50%', 
                                      backgroundColor: '#00a47c',
                                    }}></div>
                                    <Text variant="bodyMd">Customer Support</Text>
                                  </Stack>
                                  <Text variant="bodyMd" color="success">Available</Text>
                                </Stack>
                              </Stack>
                            </div>
                          </Card.Section>
                        </Card>
                      </div>
                      
                      {/* Contact Information */}
                      <div style={{ margin: '20px 0' }}>
                        <Text variant="bodyMd">
                          Need assistance? Contact our support team:   
                          <Button plain url="mailto:support@elevia-solutions.com"> support@elevia-solutions.com</Button>
                        </Text>
                      </div>
                      
                      {isAdmin && (
                        <div style={{ marginTop: '30px', borderTop: '1px solid #e6e6e6', paddingTop: '20px' }}>
                          {bypassActive ? (
                            <Button primary onClick={handleDisableBypass}>
                              Disable Bypass Mode (Admin Only)
                            </Button>
                          ) : (
                            <Button primary onClick={handleBypassMaintenance}>
                              Bypass Maintenance Mode (Admin Only)
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </Card.Section>
                </Card>
              </Layout.Section>
            </Layout>
          </div>
        </Page>
      </Frame>
  );
};

export default MaintenancePage;
